import type { IResource, IResources } from '@/assets/types/api/Resources';

export interface INewsType {
  name: string;
  code: string;
}

enum EnumNewsTypeCodes {
  NEWS = 'news',
  INDUSTRY_FOCUSES = 'industryFocuses',
  CASE_STUDIES = 'caseStudies',
  PRODUCT_SPOTLIGHTS = 'productSpotlights',
  WHITE_PAPERS = 'whitePapers',
  CATALOGS = 'catalogs',
  EVENT_INFORMATION = 'eventInformation',
  OTHERS = 'others',
  VIDEOS = 'videos',
  NEWS_EXPRESS = 'newsExpress',
}

export const useNewsStore = defineStore('newsStore', () => {
  const nuxtApp = useNuxtApp();

  /* State */

  const resources = ref<IResources>({
    pageIndex: 1,
    pageSize: 10,
    totalCount: 0,
    items: [],
  });

  const industryNews = ref<IResource>({
    title: '',
    description: '',
    typeName: '',
    pageUrl: 'javascript:;',
    imageUrl: '',
    releaseDate: '',
  });

  /* Getters */

  const totalPage = computed((): number => {
    const total = resources.value.totalCount;
    const quantity = resources.value.pageSize;
    return total <= 0
      ? 0
      : total % quantity === 0
        ? total / quantity
        : Math.floor(total / quantity) + 1;
  });

  const newsTypes = computed((): INewsType[] => {
    const { $i18n } = nuxtApp;
    const { t, locale } = $i18n;

    const isCN = locale.value === EnumLocale.ZH_CN;

    const result = isCN
      ? [
          {
            name: t('news.type-1'),
            code: EnumNewsTypeCodes.NEWS,
          },
          {
            name: t('news.type-2'),
            code: EnumNewsTypeCodes.INDUSTRY_FOCUSES,
          },
          {
            name: t('news.type-3'),
            code: EnumNewsTypeCodes.CASE_STUDIES,
          },
          {
            name: t('news.type-4'),
            code: EnumNewsTypeCodes.PRODUCT_SPOTLIGHTS,
          },
          {
            name: t('news.type-5'),
            code: EnumNewsTypeCodes.WHITE_PAPERS,
          },
          {
            name: t('news.type-7'),
            code: EnumNewsTypeCodes.EVENT_INFORMATION,
          },
          {
            name: t('news.type-8'),
            code: EnumNewsTypeCodes.OTHERS,
          },
        ]
      : [
          {
            name: t('news.type-1'),
            code: EnumNewsTypeCodes.NEWS,
          },
          {
            name: t('news.type-2'),
            code: EnumNewsTypeCodes.INDUSTRY_FOCUSES,
          },
          {
            name: t('news.type-3'),
            code: EnumNewsTypeCodes.CASE_STUDIES,
          },
          {
            name: t('news.type-4'),
            code: EnumNewsTypeCodes.PRODUCT_SPOTLIGHTS,
          },
          {
            name: t('news.type-5'),
            code: EnumNewsTypeCodes.WHITE_PAPERS,
          },
          {
            name: t('news.type-6'),
            code: EnumNewsTypeCodes.CATALOGS,
          },
          {
            name: t('news.type-9'),
            code: EnumNewsTypeCodes.VIDEOS,
          },
        ];

    return result;
  });

  const newsTypesCode = computed((): string[] =>
    newsTypes.value.map((type) => type.code)
  );

  /* Actions */

  const USE_GET_RESOURCES = async (
    keyword: Ref<string>,
    pageIndex: Ref<number>,
    typeCodes: Ref<string[]>
  ) => {
    /**
     * 搜尋資源素材資訊
     */
    const { $i18n, $notification } = nuxtApp;
    const { locale } = $i18n;

    const isCN = locale.value === EnumLocale.ZH_CN;

    const defaultTypes = isCN
      ? [
          EnumNewsTypeCodes.NEWS,
          EnumNewsTypeCodes.INDUSTRY_FOCUSES,
          EnumNewsTypeCodes.CASE_STUDIES,
          EnumNewsTypeCodes.PRODUCT_SPOTLIGHTS,
          EnumNewsTypeCodes.WHITE_PAPERS,
          EnumNewsTypeCodes.EVENT_INFORMATION,
          EnumNewsTypeCodes.OTHERS,
        ]
      : [
          EnumNewsTypeCodes.NEWS,
          EnumNewsTypeCodes.INDUSTRY_FOCUSES,
          EnumNewsTypeCodes.CASE_STUDIES,
          EnumNewsTypeCodes.PRODUCT_SPOTLIGHTS,
          EnumNewsTypeCodes.WHITE_PAPERS,
          EnumNewsTypeCodes.CATALOGS,
          EnumNewsTypeCodes.VIDEOS,
        ];

    const getResourceTypeCodes = (): string[] => {
      if (isCN) {
        return [EnumNewsTypeCodes.NEWS_EXPRESS];
      } else if (typeCodes.value.length <= 0) {
        return defaultTypes;
      } else {
        return typeCodes.value;
      }
    };

    const getInformationTypeCodes = (): string[] => {
      return typeCodes.value.length <= 0 ? defaultTypes : typeCodes.value;
    };

    const query = computed(() => ({
      websiteCode: locale.value,
      keyword: keyword.value,
      pageIndex: pageIndex.value,
      pageSize: resources.value.pageSize,
      resourceTypeCodes: getResourceTypeCodes(),
      ...(isCN && { informationTypeCodes: getInformationTypeCodes() }),
    }));

    return await useCustomFetch(() => '/marketplace/api/v1/resources/search', {
      method: 'GET',
      lazy: true,
      immediate: true,
      query,
      onResponse: ({ response }) => {
        const { _data, status } = response;
        const data = _data as IResources;

        if (status >= 200 && status < 300) {
          resources.value.pageIndex = data.pageIndex;
          resources.value.pageSize = data.pageSize;
          resources.value.totalCount = data.totalCount;
          resources.value.items = [...data.items];
        } else {
          console.error(_data);
          $notification({
            message: (_data as { message: string }).message,
            type: 'error',
          });
        }
      },
    });
  };

  const USE_GET_FEATURED_RESOURCE = async () => {
    /**
     * 搜尋資源素材資訊 (精選產業消息)
     */
    const { $i18n, $notification } = nuxtApp;
    const { locale } = $i18n;

    const isCN = locale.value === EnumLocale.ZH_CN;

    const resourceTypeCodes = isCN
      ? EnumNewsTypeCodes.NEWS_EXPRESS
      : EnumNewsTypeCodes.NEWS;

    return await useCustomFetch(
      () =>
        `/marketplace/api/v1/resources/search?websiteCode=${locale.value}&pageIndex=1&pageSize=1&informationTypeCodes=selectedNews&resourceTypeCodes=${resourceTypeCodes}`,
      {
        method: 'GET',
        lazy: true,
        immediate: true,
        onResponse: ({ response }) => {
          const { _data, status } = response;
          const data = _data as IResources;

          if (status >= 200 && status < 300) {
            if (data.items.length) {
              Object.assign(industryNews.value, data.items[0]);
            }
          } else {
            console.error(_data);
            $notification({
              message: (_data as { message: string }).message,
              type: 'error',
            });
          }
        },
      }
    );
  };

  return {
    resources,
    industryNews,
    totalPage,
    newsTypes,
    newsTypesCode,
    USE_GET_RESOURCES,
    USE_GET_FEATURED_RESOURCE,
  };
});
